import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { PreloadInfo } from "@yard/protocol";
import {
  FileArchive,
  MoreHorizontal,
  Package,
  Search,
  Smartphone,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { DeviceStatusLabel, StatusDot, type StatusTone } from "@/components/status-dot";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatBytes } from "@/lib/download";
import { type PreloadGrant, preloadApp } from "@/lib/screen/session";
import { trpc } from "@/lib/trpc";
import type { DeviceListItem } from "@/lib/types";
import { cn, platformLabel } from "@/lib/utils";

export const Route = createFileRoute("/_app/admin/preload")({
  beforeLoad: ({ context }) => {
    if (context.user.role !== "admin") throw redirect({ to: "/devices" });
  },
  component: AdminPreloadPage,
});

type AppPlatform = "ios" | "android";
type DeploymentState = "queued" | "uploading" | "success" | "failed" | "skipped";
type PreloadGrouping = "app" | "device";

type DeploymentResult = {
  state: DeploymentState;
  progress: number;
  message?: string;
};

const DEPLOYABLE_STATUSES = new Set(["ready", "present"]);

function AdminPreloadPage() {
  const queryClient = useQueryClient();
  const userId = Route.useRouteContext({ select: (context) => context.user.id });
  const fileInput = useRef<HTMLInputElement>(null);
  const deployCard = useRef<HTMLDivElement>(null);
  // Set by the header button, so only a file picked from there scrolls the
  // deploy card into view — picking from the card itself must not jump.
  const pickedFromHeader = useRef(false);
  const [file, setFile] = useState<File | null>(null);
  const [platform, setPlatform] = useState<AppPlatform | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [preloadGrouping, setPreloadGrouping] = useState<PreloadGrouping>("app");
  const [results, setResults] = useState<Record<string, DeploymentResult>>({});
  const [removeTarget, setRemoveTarget] = useState<{
    deviceId: string;
    appId: string;
    deviceName: string;
  } | null>(null);

  const { data: devices, isLoading } = useQuery({
    ...trpc.device.list.queryOptions(),
    refetchInterval: 5_000,
  });
  const { data: preloads, isLoading: preloadsLoading } = useQuery({
    ...trpc.device.preloads.queryOptions(),
    refetchInterval: 5_000,
  });
  const requestGrants = useMutation(trpc.device.preloadTokens.mutationOptions());
  const removePreload = useMutation(
    trpc.device.removePreload.mutationOptions({
      onSuccess: async () => {
        setRemoveTarget(null);
        toast.success("Preloaded app removed");
        await queryClient.invalidateQueries({ queryKey: trpc.device.preloads.queryKey() });
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const filteredDevices = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (devices ?? []).filter((device) => {
      if (!needle) return true;
      return [device.name, device.model, device.id, device.provider.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [devices, search]);

  const availableDevices = useMemo(
    () => (devices ?? []).filter((device) => platform && canSelect(device, platform)),
    [devices, platform],
  );

  const selectedCount = useMemo(
    () => (devices ?? []).filter((device) => selected.has(device.id)).length,
    [devices, selected],
  );
  const allAvailableSelected =
    availableDevices.length > 0 && availableDevices.every((device) => selected.has(device.id));

  const deviceById = useMemo(
    () => new Map((devices ?? []).map((device) => [device.id, device])),
    [devices],
  );
  const preloadsByApp = useMemo(
    () => groupPreloadsByApp(preloads ?? [], deviceById),
    [preloads, deviceById],
  );
  const preloadsByDevice = useMemo(
    () => groupPreloadsByDevice(preloads ?? [], deviceById),
    [preloads, deviceById],
  );

  const updateResult = (deviceId: string, result: Partial<DeploymentResult>) => {
    setResults((current) => ({
      ...current,
      [deviceId]: { ...current[deviceId], ...result } as DeploymentResult,
    }));
  };

  const chooseFile = (next: File | undefined) => {
    if (!next) return;
    const nextPlatform = appPlatform(next.name);
    if (!nextPlatform) {
      toast.error("Choose an .apk or .ipa file");
      return;
    }

    setFile(next);
    setPlatform(nextPlatform);
    setResults({});
    setSelected((current) => {
      const compatible = new Set(
        (devices ?? [])
          .filter((device) => device.platform === nextPlatform)
          .map((device) => device.id),
      );
      return new Set([...current].filter((id) => compatible.has(id)));
    });
  };

  const clearFile = () => {
    setFile(null);
    setPlatform(null);
    setSelected(new Set());
    setResults({});
    if (fileInput.current) fileInput.current.value = "";
  };

  const toggleDevice = (device: DeviceListItem) => {
    if (!platform || (!canSelect(device, platform) && !selected.has(device.id))) return;
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(device.id)) next.delete(device.id);
      else next.add(device.id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allAvailableSelected) {
        for (const device of availableDevices) next.delete(device.id);
      } else {
        for (const device of availableDevices) next.add(device.id);
      }
      return next;
    });
  };

  const deploy = async () => {
    if (!file || !platform || selected.size === 0) return;

    const deviceById = new Map((devices ?? []).map((device) => [device.id, device]));
    const deviceIds = [...selected].filter((id) => deviceById.get(id)?.platform === platform);
    if (deviceIds.length === 0) return;

    setDeploying(true);
    setResults(
      Object.fromEntries(deviceIds.map((deviceId) => [deviceId, { state: "queued", progress: 0 }])),
    );

    try {
      const response = await requestGrants.mutateAsync({ deviceIds });
      for (const skipped of response.skipped) {
        updateResult(skipped.deviceId, { state: "skipped", message: skipped.reason });
      }

      let cursor = 0;
      let successCount = 0;
      let failureCount = 0;
      const grants: PreloadGrant[] = response.grants;
      if (grants.length === 0) {
        toast.info("No selected devices were available");
        return;
      }
      const worker = async () => {
        while (cursor < grants.length) {
          const grant = grants[cursor++];
          if (!grant) return;
          updateResult(grant.deviceId, { state: "uploading", progress: 0 });
          try {
            await preloadApp(grant, file, (progress) =>
              updateResult(grant.deviceId, { state: "uploading", progress }),
            );
            successCount += 1;
            updateResult(grant.deviceId, { state: "success", progress: 1 });
          } catch (error) {
            failureCount += 1;
            updateResult(grant.deviceId, {
              state: "failed",
              message: errorMessage(error),
            });
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(3, grants.length) }, () => worker()));
      await queryClient.invalidateQueries({ queryKey: trpc.device.preloads.queryKey() });

      if (failureCount === 0) {
        toast.success(`${successCount} device${successCount === 1 ? "" : "s"} deployed`);
      } else {
        toast.error(`${failureCount} deployment${failureCount === 1 ? "" : "s"} failed`);
      }
    } catch (error) {
      const message = errorMessage(error);
      setResults((current) =>
        Object.fromEntries(
          Object.entries(current).map(([deviceId, result]) =>
            result.state === "queued"
              ? [deviceId, { state: "failed", progress: 0, message }]
              : [deviceId, result],
          ),
        ),
      );
      toast.error(message);
    } finally {
      setDeploying(false);
    }
  };

  const openPicker = () => {
    pickedFromHeader.current = true;
    fileInput.current?.click();
  };

  const isRemovingPreload = (preload: PreloadInfo) =>
    removePreload.isPending &&
    removePreload.variables?.deviceId === preload.deviceId &&
    removePreload.variables.appId === preload.appId;

  const preloadCount = preloads?.length ?? 0;

  const summary = `${preloadCount.toLocaleString()} installed ${countNoun(preloadCount, "copy", "copies")} across ${preloadsByApp.length.toLocaleString()} ${countNoun(preloadsByApp.length, "app", "apps")} and ${preloadsByDevice.length.toLocaleString()} ${countNoun(preloadsByDevice.length, "device", "devices")}.`;

  return (
    <div className="flex flex-col">
      <PageHeader title="Preloaded apps" />

      {/* Vercel's resource-page head (Environment Variables): an H1 with its
          primary action on the same row, a grey description under both. */}
      <div className="mb-6 grid gap-3 sm:gap-4">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-gray-1000 text-heading-20 sm:text-heading-24">Preloaded apps</h1>
          <Button
            className="h-10 shrink-0"
            prefix={<Upload />}
            disabled={deploying}
            onClick={openPicker}
          >
            Deploy app
          </Button>
        </div>
        <p className="max-w-3xl text-copy-14 text-gray-900">
          Deploy APKs and IPAs across the farm, or remove an existing preload from a device.
        </p>
      </div>

      <div className="flex flex-col gap-12">
        <section aria-labelledby="preloads-heading" className="grid min-w-0 gap-4">
          <h2 id="preloads-heading" className="sr-only">
            Installed preloads
          </h2>

          {preloadsLoading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-background-100 py-12 text-copy-14 text-gray-900 shadow-border">
              <Spinner />
              Loading preloads…
            </div>
          ) : preloads?.length === 0 ? (
            <EmptyState
              icon={<Package />}
              title="No apps are preloaded"
              description="A package you deploy stays on its devices and is restored during cleanup if someone removes it."
            >
              <Button
                variant="secondary"
                className="h-10"
                prefix={<Upload />}
                disabled={deploying}
                onClick={openPicker}
              >
                Deploy app
              </Button>
            </EmptyState>
          ) : (
            <Tabs
              value={preloadGrouping}
              onValueChange={(value) => setPreloadGrouping(value as PreloadGrouping)}
              className="min-w-0 gap-5"
            >
              {/* The underline row carries the summary at its far end, so the
                  rule runs under both rather than stopping where the tabs do. */}
              <div className="flex flex-wrap items-center gap-x-6 shadow-[inset_0_-1px_0_var(--geist-accents-2)]">
                <TabsList aria-label="Group preloads" className="shadow-none">
                  <TabsTrigger value="app">By app</TabsTrigger>
                  <TabsTrigger value="device">By device</TabsTrigger>
                </TabsList>
                <p className="ml-auto hidden text-label-14 text-gray-900 tabular-nums sm:block">
                  {summary}
                </p>
              </div>
              <p className="-mt-1 text-label-14 text-gray-900 tabular-nums sm:hidden">{summary}</p>

              <TabsContent value="app" className="grid grid-cols-[minmax(0,1fr)] gap-4">
                {preloadsByApp.map((group) => (
                  <PreloadGroup
                    key={group.key}
                    icon={<Package />}
                    title={<span className="font-mono text-label-14-mono">{group.appId}</span>}
                    titleText={group.appId}
                    detail={packageSummary(group.preloads)}
                    count={group.preloads.length}
                    countLabel={countNoun(group.preloads.length, "device", "devices")}
                    platform={group.platform}
                  >
                    {group.preloads.map((preload) => {
                      const target = deviceById.get(preload.deviceId);
                      const deviceName = deviceDisplayName(target, preload.deviceId);
                      return (
                        <PreloadRow
                          key={`${preload.deviceId}:${preload.appId}`}
                          label={deviceName}
                          detail={[
                            target?.provider.name,
                            preload.filename,
                            formatBytes(preload.size),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          preload={preload}
                          deviceName={deviceName}
                          target={target}
                          status={
                            target && (
                              <DeviceStatusLabel
                                status={target.status}
                                mine={target.reservation?.userId === userId}
                                className="shrink-0 sm:w-24"
                              />
                            )
                          }
                          removing={removePreload.isPending}
                          isRemoving={isRemovingPreload(preload)}
                          onRemove={setRemoveTarget}
                        />
                      );
                    })}
                  </PreloadGroup>
                ))}
              </TabsContent>

              <TabsContent value="device" className="grid grid-cols-[minmax(0,1fr)] gap-4">
                {preloadsByDevice.map((group) => {
                  const target = deviceById.get(group.deviceId);
                  const deviceName = deviceDisplayName(target, group.deviceId);
                  return (
                    <PreloadGroup
                      key={group.deviceId}
                      icon={<Smartphone />}
                      title={deviceName}
                      titleText={deviceName}
                      detail={target?.provider.name ?? group.deviceId}
                      count={group.preloads.length}
                      countLabel={countNoun(group.preloads.length, "app", "apps")}
                      platform={group.platform}
                      status={
                        target && (
                          <DeviceStatusLabel
                            status={target.status}
                            mine={target.reservation?.userId === userId}
                          />
                        )
                      }
                    >
                      {group.preloads.map((preload) => (
                        <PreloadRow
                          key={`${preload.deviceId}:${preload.appId}`}
                          label={
                            <span className="font-mono text-label-14-mono">{preload.appId}</span>
                          }
                          detail={`${preload.filename} · ${formatBytes(preload.size)}`}
                          preload={preload}
                          deviceName={deviceName}
                          target={target}
                          removing={removePreload.isPending}
                          isRemoving={isRemovingPreload(preload)}
                          onRemove={setRemoveTarget}
                        />
                      ))}
                    </PreloadGroup>
                  );
                })}
              </TabsContent>
            </Tabs>
          )}
        </section>

        <div className="grid gap-3">
          {/* A Vercel deploy-flow card: the fields stacked on white, then the
              footer strip with the running count and the primary action. */}
          <Card
            ref={deployCard}
            role="region"
            aria-labelledby="deploy-heading"
            className="scroll-mt-6 gap-0 rounded-lg py-0"
          >
            <div className="grid gap-8 p-5 sm:p-6">
              <div className="grid gap-3">
                <h2 id="deploy-heading" className="text-gray-1000 text-heading-20">
                  Deploy a package
                </h2>
                <CardDescription>
                  APK files can only target Android devices. IPA files can only target iOS devices.
                  The farm retains every preload and restores it during cleanup if it is removed.
                </CardDescription>
              </div>

              <div className="grid gap-3">
                <FieldHeading title="Application package" />
                <input
                  ref={fileInput}
                  type="file"
                  accept=".apk,.ipa"
                  className="sr-only"
                  onChange={(event) => {
                    chooseFile(event.target.files?.[0]);
                    if (pickedFromHeader.current) {
                      pickedFromHeader.current = false;
                      deployCard.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                />
                {!file ? (
                  <button
                    type="button"
                    className={cn(
                      "flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed px-6 py-10 text-center outline-none transition-colors focus-visible:shadow-focus-ring",
                      dragging
                        ? "border-blue-700 bg-blue-100"
                        : "border-gray-alpha-500 bg-background-200 hover:border-gray-alpha-600 hover:bg-gray-100",
                    )}
                    onClick={() => fileInput.current?.click()}
                    onDragEnter={(event) => {
                      event.preventDefault();
                      setDragging(true);
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      setDragging(false);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragging(false);
                      chooseFile(event.dataTransfer.files[0]);
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "flex size-10 items-center justify-center rounded-lg bg-background-100 shadow-border",
                        dragging ? "text-blue-900" : "text-gray-1000",
                      )}
                    >
                      <Upload className="size-4" />
                    </span>
                    <span className="grid gap-1">
                      <span className="font-medium text-gray-1000 text-label-14">
                        Drop an APK or IPA here
                      </span>
                      <span className="text-copy-14 text-gray-900">or click to browse</span>
                    </span>
                  </button>
                ) : (
                  <div className="flex items-center gap-3 rounded-lg bg-background-100 py-3 pr-2 pl-3 shadow-border sm:gap-4 sm:pl-4">
                    <span
                      aria-hidden="true"
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-1000"
                    >
                      <FileArchive className="size-4" />
                    </span>
                    <div className="grid min-w-0 flex-1">
                      <p
                        className="truncate font-medium text-gray-1000 text-label-14"
                        title={file.name}
                      >
                        {file.name}
                      </p>
                      <p className="text-gray-900 text-label-13 tabular-nums">
                        {formatBytes(file.size)}
                      </p>
                    </div>
                    <Badge variant="gray-subtle" size="sm">
                      {platformLabel(platform ?? "")}
                    </Badge>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon-sm"
                      className="pointer-coarse:size-10"
                      aria-label="Remove selected file"
                      onClick={clearFile}
                    >
                      <X />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid gap-4">
                <FieldHeading
                  title="Target devices"
                  description={
                    platform
                      ? `Select available ${platformLabel(platform)} devices. Devices in use are skipped at deployment time.`
                      : "Choose a package first to filter devices by platform."
                  }
                />
                {/* Vercel's bulk toolbar (Deployment Protection): select-all, the
                    search that flexes, then the secondary action. On a phone the
                    search takes its own row first. */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                  <label
                    htmlFor="deploy-select-available"
                    className="inline-flex min-h-10 cursor-pointer items-center gap-2 text-gray-1000 text-label-14 has-disabled:cursor-not-allowed has-disabled:text-gray-700"
                  >
                    <Checkbox
                      id="deploy-select-available"
                      className="pointer-coarse:size-5"
                      checked={
                        allAvailableSelected ? true : selectedCount > 0 ? "indeterminate" : false
                      }
                      disabled={!platform || availableDevices.length === 0 || deploying}
                      onCheckedChange={selectAll}
                    />
                    Select available
                  </label>
                  <Input
                    className="order-first h-10 basis-full sm:order-none sm:basis-0 sm:flex-1 [&>input]:h-full"
                    prefix={<Search className="size-4" />}
                    prefixStyling={false}
                    placeholder="Filter devices…"
                    aria-label="Filter devices"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    className="ml-auto h-10 sm:ml-0"
                    disabled={selectedCount === 0 || deploying}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear selection
                  </Button>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center gap-2 rounded-lg py-10 text-copy-14 text-gray-900 shadow-border">
                    <Spinner />
                    Loading devices…
                  </div>
                ) : filteredDevices.length === 0 ? (
                  <EmptyState
                    secondary
                    className="py-10"
                    description="No devices match this filter."
                  />
                ) : (
                  <ul className="divide-y divide-gray-alpha-400 overflow-hidden rounded-lg bg-background-100 shadow-border">
                    {filteredDevices.map((device) => {
                      const compatible = Boolean(platform && device.platform === platform);
                      const selectable = Boolean(platform && canSelect(device, platform));
                      const isSelected = selected.has(device.id);
                      // The status word already says "In use", "Offline" and the
                      // like; the note only adds what the status cannot — that the
                      // package does not fit this device, or its provider is gone.
                      const note = !platform
                        ? null
                        : !compatible
                          ? `${platformLabel(platform)} only`
                          : !device.reservation && device.provider.status !== "online"
                            ? "Provider offline"
                            : null;
                      const checkboxId = `deploy-target-${device.id}`;
                      return (
                        <li key={device.id}>
                          <label
                            htmlFor={checkboxId}
                            className={cn(
                              "flex min-h-16 items-center gap-4 px-4 py-3 transition-colors",
                              selectable && !deploying
                                ? "cursor-pointer hover:bg-gray-alpha-100"
                                : "cursor-not-allowed",
                            )}
                          >
                            <Checkbox
                              id={checkboxId}
                              className="pointer-coarse:size-5"
                              checked={isSelected}
                              disabled={(!selectable && !isSelected) || deploying}
                              onCheckedChange={() => toggleDevice(device)}
                            />
                            <span
                              className={cn(
                                "flex min-w-0 flex-1 items-center gap-4",
                                !selectable && "opacity-55",
                              )}
                            >
                              <span className="grid min-w-0 flex-1">
                                <span className="truncate font-medium text-gray-1000 text-label-14">
                                  {device.name ?? device.model ?? device.id}
                                </span>
                                <span className="truncate text-copy-14 text-gray-900">
                                  {platformLabel(device.platform)} · {device.model ?? device.id} ·{" "}
                                  {device.provider.name}
                                </span>
                              </span>
                              <span className="grid shrink-0 justify-items-end gap-1">
                                <DeviceStatusLabel
                                  status={device.status}
                                  mine={device.reservation?.userId === userId}
                                />
                                {note && (
                                  <span className="text-gray-900 text-label-13">{note}</span>
                                )}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {Object.keys(results).length > 0 && (
                <div className="grid gap-3">
                  <FieldHeading
                    title="Deployment results"
                    description="The provider retains the package and repairs the preload during cleanup if someone removes the app during a session."
                  />
                  <ul className="divide-y divide-gray-alpha-400 overflow-hidden rounded-lg bg-background-100 shadow-border">
                    {Object.entries(results).map(([deviceId, result]) => {
                      const device = devices?.find((candidate) => candidate.id === deviceId);
                      const name = device?.name ?? device?.model ?? deviceId;
                      const percent = Math.round(result.progress * 100);
                      return (
                        <li key={deviceId} className="grid min-h-14 content-center gap-2 px-4 py-3">
                          <div className="flex items-center justify-between gap-4">
                            <span className="min-w-0 truncate font-medium text-gray-1000 text-label-14">
                              {name}
                            </span>
                            <ResultStatus result={result} />
                          </div>
                          {result.state === "uploading" && (
                            <div
                              role="progressbar"
                              aria-label={`Uploading to ${name}`}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={percent}
                              className="h-1.5 overflow-hidden rounded-full bg-gray-200"
                            >
                              <div
                                className="h-full rounded-full bg-blue-700 transition-[width] duration-100 ease-in"
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                          )}
                          {result.message && (
                            <p className="-mt-1 truncate text-gray-900 text-label-13">
                              {result.message}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <CardFooter className="mb-0 flex-col items-stretch gap-3 px-5 sm:flex-row sm:items-center sm:gap-4 sm:px-6">
              <p className="tabular-nums">
                {selectedCount} device{selectedCount === 1 ? "" : "s"} selected
              </p>
              <Button
                type="button"
                size="small"
                className="w-full pointer-coarse:h-10 sm:ml-auto sm:w-auto"
                loading={deploying}
                disabled={!file || !platform || selectedCount === 0}
                onClick={() => void deploy()}
              >
                {deploying
                  ? "Deploying…"
                  : `Deploy to ${selectedCount} device${selectedCount === 1 ? "" : "s"}`}
              </Button>
            </CardFooter>
          </Card>

          <p className="text-copy-13 text-gray-900">
            Only idle devices are eligible. The provider checks again before install, then keeps the
            preload under farm control and repairs it during cleanup before the device is released.
          </p>
        </div>
      </div>

      <Dialog open={removeTarget !== null} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Remove this preloaded app?</DialogTitle>
            <DialogDescription>
              {removeTarget
                ? `${removeTarget.appId} will be uninstalled from ${removeTarget.deviceName} and will no longer be restored during cleanup.`
                : "The app will be uninstalled and removed from preload policy."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="error"
              loading={removePreload.isPending}
              disabled={!removeTarget}
              onClick={() => removeTarget && removePreload.mutate(removeTarget)}
            >
              Remove app
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** A field's name over an optional grey line saying what it wants. */
function FieldHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="grid gap-0.5">
      <h3 className="font-medium text-gray-1000 text-label-14">{title}</h3>
      {description && <p className="text-copy-14 text-gray-900">{description}</p>}
    </div>
  );
}

/**
 * One app (or one device) and its rows, as a group card on Vercel's
 * Environment Variables page: a header with a 24px mark, the name and a grey
 * count, then rows inset 8px whose hover fill has rounded ends.
 */
function PreloadGroup({
  icon,
  title,
  titleText,
  detail,
  count,
  countLabel,
  platform,
  status,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  titleText: string;
  detail: string;
  count: number;
  countLabel: string;
  platform: PreloadInfo["platform"];
  status?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={titleText}
      className="rounded-lg bg-background-100 px-2 pb-3 shadow-border"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 pt-5 pb-4">
        <div className="flex min-w-0 flex-1 basis-full items-center gap-3 sm:basis-auto">
          <span
            aria-hidden="true"
            className="flex size-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-gray-1000 shadow-border [&_svg]:size-3.5"
          >
            {icon}
          </span>
          <p className="truncate font-medium text-gray-1000 text-label-14" title={titleText}>
            {title}
          </p>
          <span
            className="shrink-0 text-gray-900 text-label-14 tabular-nums"
            title={`${count} ${countLabel}`}
          >
            {count}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-3 pl-9 text-gray-900 text-label-14 sm:pl-0">
          {status}
          <span className="truncate" title={detail}>
            {detail}
          </span>
          <span className="shrink-0">{platformLabel(platform)}</span>
        </div>
      </div>
      <ul>{children}</ul>
    </section>
  );
}

function PreloadRow({
  label,
  detail,
  preload,
  deviceName,
  target,
  status,
  removing,
  isRemoving,
  onRemove,
}: {
  label: ReactNode;
  detail: string;
  preload: PreloadInfo;
  deviceName: string;
  target: DeviceListItem | undefined;
  /** The device's state, where the row is a device rather than an app. */
  status?: ReactNode;
  removing: boolean;
  isRemoving: boolean;
  onRemove: (target: { deviceId: string; appId: string; deviceName: string }) => void;
}) {
  const unavailableReason = preloadUnavailableReason(target);
  const blockedReason =
    unavailableReason ?? (removing && !isRemoving ? "Another preload is being removed" : null);

  return (
    <li className="flex min-h-11 items-center gap-3 rounded-md py-1 pr-1 pl-3 transition-colors hover:bg-gray-alpha-100 sm:min-h-10 sm:gap-4">
      {/* Two lines on a phone, one row from sm: the detail moves to the right
          the way Vercel right-aligns a variable's environments. */}
      <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-center sm:gap-4">
        <p className="truncate text-gray-1000 text-label-14 sm:min-w-0 sm:flex-1">{label}</p>
        <p
          className="truncate text-gray-900 text-label-13 sm:max-w-[55%] sm:text-label-14"
          title={detail}
        >
          {detail}
        </p>
      </div>
      {status}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="tertiary"
            size="icon-sm"
            className="shrink-0 pointer-coarse:size-10"
            aria-label={`Actions for ${preload.appId} on ${deviceName}`}
          >
            {isRemoving ? <Spinner /> : <MoreHorizontal />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem
            variant="error"
            disabled={Boolean(unavailableReason) || removing}
            aria-label={`Remove ${preload.appId} from ${deviceName}`}
            onSelect={() =>
              onRemove({
                deviceId: preload.deviceId,
                appId: preload.appId,
                deviceName,
              })
            }
          >
            <Trash2 />
            Remove preload
          </DropdownMenuItem>
          {blockedReason && (
            <p className="px-2 pt-1 pb-1.5 text-gray-900 text-label-13">{blockedReason}</p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function groupPreloadsByApp(
  preloads: readonly PreloadInfo[],
  deviceById: ReadonlyMap<string, DeviceListItem>,
) {
  const groups = new Map<
    string,
    {
      key: string;
      appId: string;
      platform: PreloadInfo["platform"];
      preloads: PreloadInfo[];
    }
  >();

  for (const preload of preloads) {
    const key = `${preload.platform}:${preload.appId}`;
    const existing = groups.get(key);
    if (existing) existing.preloads.push(preload);
    else {
      groups.set(key, {
        key,
        appId: preload.appId,
        platform: preload.platform,
        preloads: [preload],
      });
    }
  }

  return [...groups.values()]
    .sort((a, b) => a.appId.localeCompare(b.appId))
    .map((group) => ({
      ...group,
      preloads: group.preloads.sort((a, b) =>
        deviceDisplayName(deviceById.get(a.deviceId), a.deviceId).localeCompare(
          deviceDisplayName(deviceById.get(b.deviceId), b.deviceId),
        ),
      ),
    }));
}

function groupPreloadsByDevice(
  preloads: readonly PreloadInfo[],
  deviceById: ReadonlyMap<string, DeviceListItem>,
) {
  const groups = new Map<
    string,
    { deviceId: string; platform: PreloadInfo["platform"]; preloads: PreloadInfo[] }
  >();

  for (const preload of preloads) {
    const existing = groups.get(preload.deviceId);
    if (existing) existing.preloads.push(preload);
    else {
      groups.set(preload.deviceId, {
        deviceId: preload.deviceId,
        platform: preload.platform,
        preloads: [preload],
      });
    }
  }

  return [...groups.values()]
    .sort((a, b) =>
      deviceDisplayName(deviceById.get(a.deviceId), a.deviceId).localeCompare(
        deviceDisplayName(deviceById.get(b.deviceId), b.deviceId),
      ),
    )
    .map((group) => ({
      ...group,
      preloads: group.preloads.sort((a, b) => a.appId.localeCompare(b.appId)),
    }));
}

function packageSummary(preloads: readonly PreloadInfo[]): string {
  const variants = new Map(preloads.map((preload) => [preload.sha256, preload]));
  if (variants.size !== 1) return `${variants.size} package variants`;
  const preload = variants.values().next().value;
  return preload ? `${preload.filename} · ${formatBytes(preload.size)}` : "";
}

function deviceDisplayName(device: DeviceListItem | undefined, fallback: string): string {
  return device?.name ?? device?.model ?? fallback;
}

function preloadUnavailableReason(device: DeviceListItem | undefined): string | null {
  if (!device) return "Device is unavailable";
  if (device.reservation) return "Device is in use";
  if (device.provider.status !== "online") return "Provider is offline";
  if (!DEPLOYABLE_STATUSES.has(device.status)) return `Device is ${device.status}`;
  return null;
}

function countNoun(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function appPlatform(filename: string): AppPlatform | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".apk")) return "android";
  if (lower.endsWith(".ipa")) return "ios";
  return null;
}

function canSelect(device: DeviceListItem, platform: AppPlatform): boolean {
  return (
    device.platform === platform &&
    device.provider.status === "online" &&
    !device.reservation &&
    DEPLOYABLE_STATUSES.has(device.status)
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Deployment failed";
}

const RESULT_STATUS: Record<DeploymentState, { label: string; tone: StatusTone; pulse?: boolean }> =
  {
    queued: { label: "Queued", tone: "muted" },
    uploading: { label: "Uploading", tone: "info", pulse: true },
    success: { label: "Installed", tone: "success" },
    failed: { label: "Failed", tone: "destructive" },
    skipped: { label: "Skipped", tone: "muted" },
  };

function ResultStatus({ result }: { result: DeploymentResult }) {
  const s = RESULT_STATUS[result.state];
  return (
    <span className="inline-flex shrink-0 items-center gap-2 text-gray-1000 text-label-14">
      <StatusDot tone={s.tone} pulse={s.pulse} />
      {s.label}
      {result.state === "uploading" && (
        <span className="text-gray-900 tabular-nums">{Math.round(result.progress * 100)}%</span>
      )}
    </span>
  );
}
